import React from 'react';
import { SingleSelectComboBox } from '../../../components/SingleSelectComboBox';
import { useCustodians } from '../../../hooks/useCustodians';
import { PIIDetectionMode, type Custodian } from '../../../types/collection';
import { isOrganizationProject, type ProjectDetail } from '../../../types/project';

export interface CollectionMetadata {
  custodianName: string;
  name: string;
  description: string;
  ritm: string;
  evidence: string;
  release: string;
  extractFromContainers: boolean;
  extractAttachmentAndEmbeddedDocumentsFromEmails: boolean;
  extractImagesFromEmails: boolean;
  extractDocumentsFromFiles: boolean;
  extractImagesFromFiles: boolean;
  imagePresentationsWithNotes: boolean;
  cancelTranscription: boolean;
  cancelExtractEntities: boolean;
  cancelPdf: boolean;
  cancelOcr: boolean;
  piiDetectionMode: PIIDetectionMode;
  acceptTermsAndConditions: boolean;
}

export function defaultMetadata(projectStatus: string): CollectionMetadata {
  return {
    custodianName: '',
    name: '',
    description: '',
    ritm: '',
    evidence: '',
    release: '',
    extractFromContainers: true,
    extractAttachmentAndEmbeddedDocumentsFromEmails: true,
    extractImagesFromEmails: false,
    extractDocumentsFromFiles: false,
    extractImagesFromFiles: false,
    imagePresentationsWithNotes: false,
    cancelTranscription: false,
    cancelExtractEntities: false,
    cancelPdf: false,
    cancelOcr: false,
    piiDetectionMode: PIIDetectionMode.Standard,
    acceptTermsAndConditions: projectStatus === 'trial',
  };
}

export interface MetadataErrors {
  custodianName?: string;
  name?: string;
}

interface Props {
  projectId: string;
  project: ProjectDetail;
  metadata: CollectionMetadata;
  onChange: (metadata: CollectionMetadata) => void;
  errors: MetadataErrors;
  isInternalUser: boolean;
  disabled: boolean;
}

export function Step1MetadataForm({ projectId, project, metadata, onChange, errors, isInternalUser, disabled }: Props) {
  const { data: custodians = [] } = useCustodians(projectId);

  const set = <K extends keyof CollectionMetadata>(key: K, value: CollectionMetadata[K]) =>
    onChange({ ...metadata, [key]: value });

  // The chosen custodian — an existing record or a free-typed name.
  const selectedCustodian: Custodian | null = metadata.custodianName
    ? (custodians.find(c => c.name === metadata.custodianName)
        ?? { id: metadata.custodianName, name: metadata.custodianName, projectId })
    : null;

  const isOrg = isOrganizationProject(project);
  const isInnovativeDriven = isInternalUser;
  const hasSkipFlags = !!((window as any).__features?.processingSkipFlags);

  return (
    <div>
      <div className="form-group">
        <h4>Project:{' '}
          <small><i className="fas fa-gavel" aria-hidden="true" /> {project.name}</small>
        </h4>
      </div>

      <div className={`form-group${errors.custodianName ? ' has-error' : ''}`}>
        {errors.custodianName && <span className="help-block">{errors.custodianName}</span>}
        <div className="row">
          <div className="col-md-6">
            <SingleSelectComboBox<Custodian>
              id="col-custodian"
              value={selectedCustodian}
              onChange={c => set('custodianName', c?.name ?? '')}
              onFreeEntry={value => set('custodianName', value)}
              getKey={c => c.id}
              getLabel={c => c.name}
              items={custodians}
              icon="fa-user"
              placeholder="Who does this data belong to?"
              disabled={disabled}
              testId="add-collection-custodian-select"
            />
          </div>
        </div>
      </div>

      <div className={`form-group${errors.name ? ' has-error' : ''}`}>
        {errors.name && <span className="help-block">{errors.name}</span>}
        <input
          type="text"
          placeholder="Collection Name"
          className="form-control"
          data-testid="add-collection-name-input"
          value={metadata.name}
          onChange={e => set('name', e.target.value)}
          maxLength={255}
          disabled={disabled}
          autoFocus
        />
      </div>

      <div className="form-group">
        <input
          type="text"
          placeholder="Collection Description"
          className="form-control"
          data-testid="add-collection-description-input"
          value={metadata.description}
          onChange={e => set('description', e.target.value)}
          maxLength={255}
          disabled={disabled}
        />
      </div>

      {isOrg && (
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <input type="text" placeholder="Identifier 1" className="form-control" data-testid="add-collection-identifier1-input"
                value={metadata.ritm} onChange={e => set('ritm', e.target.value)} maxLength={255} disabled={disabled} />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <input type="text" placeholder="Identifier 2" className="form-control" data-testid="add-collection-identifier2-input"
                value={metadata.evidence} onChange={e => set('evidence', e.target.value)} maxLength={255} disabled={disabled} />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <input type="text" placeholder="Identifier 3" className="form-control" data-testid="add-collection-identifier3-input"
                value={metadata.release} onChange={e => set('release', e.target.value)} maxLength={255} disabled={disabled} />
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-md-6">
          <label>Email</label>
          <div className="form-group select-disabled">
            <Checkbox id="attachmentsAndEmbeddedDocumentsFromEmail" testId="add-collection-email-attachments-checkbox" label="Extract Attachments and Embedded Documents"
              checked={metadata.extractAttachmentAndEmbeddedDocumentsFromEmails}
              onChange={v => set('extractAttachmentAndEmbeddedDocumentsFromEmails', v)} disabled={disabled} />
            <Checkbox id="embeddedImagesFromEmail" testId="add-collection-email-images-checkbox" label="Extract Embedded Images"
              checked={metadata.extractImagesFromEmails}
              onChange={v => set('extractImagesFromEmails', v)} disabled={disabled} />
          </div>
        </div>
        <div className="col-md-6">
          <label>Document</label>
          <div className="form-group select-disabled">
            <Checkbox id="files" testId="add-collection-container-files-checkbox" label="Extract Files from Containers"
              checked={metadata.extractFromContainers}
              onChange={v => set('extractFromContainers', v)} disabled={disabled} />
            <Checkbox id="embeddedDocumentsFromFile" testId="add-collection-embedded-documents-checkbox" label="Extract Embedded Documents"
              checked={metadata.extractDocumentsFromFiles}
              onChange={v => set('extractDocumentsFromFiles', v)} disabled={disabled} />
            <Checkbox id="embeddedImagesFromFile" testId="add-collection-file-images-checkbox" label="Extract Embedded Images"
              checked={metadata.extractImagesFromFiles}
              onChange={v => set('extractImagesFromFiles', v)} disabled={disabled} />
            <Checkbox id="notes" testId="add-collection-presentation-notes-checkbox" label="Image Presentations with Notes"
              checked={metadata.imagePresentationsWithNotes}
              onChange={v => set('imagePresentationsWithNotes', v)} disabled={disabled} />
          </div>
        </div>
      </div>

      {isInnovativeDriven && (
        <div className="row">
          <div className="col-md-6">
            <label>Advanced Analytics &amp; AI</label>
            <div className="form-group select-disabled">
              <div className="radio radio-primary">
                <input id="pii-mode-standard" name="piiDetectionMode" type="radio" data-testid="add-collection-pii-standard-radio"
                  checked={metadata.piiDetectionMode === PIIDetectionMode.Standard}
                  onChange={() => set('piiDetectionMode', PIIDetectionMode.Standard)} disabled={disabled} />
                <label htmlFor="pii-mode-standard">Standard</label>
              </div>
              <div className="radio radio-primary">
                <input id="pii-mode-enhanced" name="piiDetectionMode" type="radio" data-testid="add-collection-pii-enhanced-radio"
                  checked={metadata.piiDetectionMode === PIIDetectionMode.Enhanced}
                  onChange={() => set('piiDetectionMode', PIIDetectionMode.Enhanced)}
                  disabled={disabled || !project.enhancedPiiDetection} />
                <label htmlFor="pii-mode-enhanced">Enhanced</label>
                {!project.enhancedPiiDetection && (
                  <div><small style={{ color: 'gray' }}>See your admin to enable this paid add-on.</small></div>
                )}
              </div>
            </div>
          </div>
          {hasSkipFlags && (
            <div className="col-md-6">
              <label>Streamline Processing</label>
              <div className="form-group select-disabled">
                <Checkbox id="cancelTranscription" testId="add-collection-skip-transcription-checkbox" label="Skip Audio/Video Transcription"
                  checked={metadata.cancelTranscription} onChange={v => set('cancelTranscription', v)} disabled={disabled} />
                <Checkbox id="cancelExtractEntities" testId="add-collection-skip-entities-checkbox" label="Skip Entity Extraction"
                  checked={metadata.cancelExtractEntities} onChange={v => set('cancelExtractEntities', v)} disabled={disabled} />
                <Checkbox id="cancelPdf" testId="add-collection-skip-pdf-checkbox" label="Skip PDF Generation"
                  checked={metadata.cancelPdf} onChange={v => set('cancelPdf', v)} disabled={disabled} />
                <Checkbox id="cancelOcr" testId="add-collection-skip-ocr-checkbox" label="Skip OCR Text Extraction"
                  checked={metadata.cancelOcr} onChange={v => set('cancelOcr', v)} disabled={disabled} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Checkbox({ id, label, checked, onChange, disabled, testId }: {
  id: string; label: string; checked: boolean; onChange: (v: boolean) => void; disabled: boolean; testId: string;
}) {
  return (
    <div className="checkbox checkbox-primary">
      <input id={id} type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} data-testid={testId} />
      <label htmlFor={id}>{label}</label>
    </div>
  );
}
